// Storage Manager - Unified storage for localStorage + IndexedDB
// Handles trips, photos, and all heavy content

class StorageManager {
  constructor() {
    this.db = null;
    this.dbName = 'TravelAppDB';
    this.dbVersion = 3;
    this.isInitialized = false;
  }

  // ========================================
  // INITIALIZATION
  // ========================================

  async initialize() {
    if (this.isInitialized) {
      return this.db;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('❌ IndexedDB failed to open:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        console.log('✅ IndexedDB initialized successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        console.log('🔧 IndexedDB: Creating object stores...');

        // Create 'trips' object store
        if (!db.objectStoreNames.contains('trips')) {
          const tripStore = db.createObjectStore('trips', { keyPath: 'tripId' });
          tripStore.createIndex('status', 'status', { unique: false });
          tripStore.createIndex('createdAt', 'createdAt', { unique: false });
          tripStore.createIndex('destination', 'destination.city', { unique: false });
          console.log('  ✓ Created "trips" store');
        }

        // Create 'photos' object store
        if (!db.objectStoreNames.contains('photos')) {
          const photoStore = db.createObjectStore('photos', { keyPath: 'id' });
          photoStore.createIndex('tripId', 'tripId', { unique: false });
          photoStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('  ✓ Created "photos" store');
        }

        // Create 'thumbnails' object store
        if (!db.objectStoreNames.contains('thumbnails')) {
          db.createObjectStore('thumbnails', { keyPath: 'photoId' });
          console.log('  ✓ Created "thumbnails" store');
        }

        // Create 'voiceNotes' object store (for future)
        if (!db.objectStoreNames.contains('voiceNotes')) {
          const voiceStore = db.createObjectStore('voiceNotes', { keyPath: 'id' });
          voiceStore.createIndex('tripId', 'tripId', { unique: false });
          voiceStore.createIndex('noteId', 'noteId', { unique: false });
          console.log('  ✓ Created "voiceNotes" store');
        }

        // v2: traveller profile store (singleton, key = 'profile')
        if (!db.objectStoreNames.contains('userProfile')) {
          db.createObjectStore('userProfile');
          console.log('  ✓ Created "userProfile" store');
        }

        // v3: file handles store — persists FileSystemFileHandle per trip
        if (!db.objectStoreNames.contains('fileHandles')) {
          db.createObjectStore('fileHandles'); // keyPath is the tripId (explicit key)
          console.log('  ✓ Created "fileHandles" store');
        }

        console.log('✅ IndexedDB schema created');
      };
    });
  }

  // ========================================
  // TRIPS (IndexedDB)
  // ========================================

  async saveTrip(tripData) {
    if (!this.isInitialized) await this.initialize();

    const transaction = this.db.transaction(['trips'], 'readwrite');
    const store = transaction.objectStore('trips');

    return new Promise((resolve, reject) => {
      const request = store.put(tripData);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getTrip(tripId) {
    if (!this.isInitialized) await this.initialize();

    const transaction = this.db.transaction(['trips'], 'readonly');
    const store = transaction.objectStore('trips');

    return new Promise((resolve, reject) => {
      const request = store.get(tripId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ── Per-account ownership ──────────────────────────────────────────────────
  // Trips live in one device-wide IndexedDB. To keep accounts separate we tag
  // each trip with ownerEmail and only return the current owner's trips. When no
  // owner is set (not logged in / legacy), behaviour is the OLD device-global one
  // — so nothing changes for flows without an authenticated user.

  getOwner() {
    try { return localStorage.getItem('wayfare_owner') || null; } catch { return null; }
  }

  setOwner(email) {
    try {
      if (email) localStorage.setItem('wayfare_owner', email);
      else localStorage.removeItem('wayfare_owner');
    } catch { /* ignore */ }
  }

  // Unfiltered — internal use (migration, deletes).
  async getAllTripsRaw() {
    if (!this.isInitialized) await this.initialize();
    const transaction = this.db.transaction(['trips'], 'readonly');
    const store = transaction.objectStore('trips');
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllTrips() {
    const all = await this.getAllTripsRaw();
    const owner = this.getOwner();
    if (!owner) return all; // not logged in → legacy behaviour
    return all.filter(t => (t.ownerEmail || null) === owner);
  }

  // One-time migration on login: any trip without an owner is claimed by the
  // current user (the device's existing trips belong to whoever is using it).
  // After this runs, every trip is tagged and scoping is fully in effect.
  async claimUntaggedTrips() {
    const owner = this.getOwner();
    if (!owner) return;
    const all = await this.getAllTripsRaw();
    const untagged = all.filter(t => !t.ownerEmail);
    if (untagged.length === 0) return;
    for (const t of untagged) {
      t.ownerEmail = owner;
      await this.saveTrip(t);
    }
    // Rebuild this owner's index from their (now tagged) trips
    const owned = (await this.getAllTripsRaw()).filter(t => t.ownerEmail === owner);
    const active = owned.find(t => t.status === 'active');
    this.saveTripsIndex({
      activeTrip: active?.tripId || null,
      tripIds: owned.map(t => t.tripId),
      lastModified: new Date().toISOString(),
    });
  }

  async getTripsByStatus(status) {
    if (!this.isInitialized) await this.initialize();

    const transaction = this.db.transaction(['trips'], 'readonly');
    const store = transaction.objectStore('trips');
    const index = store.index('status');

    return new Promise((resolve, reject) => {
      const request = index.getAll(IDBKeyRange.only(status));
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async updateTripStatus(tripId, newStatus) {
    if (!this.isInitialized) await this.initialize();

    // Get trip first
    const trip = await this.getTrip(tripId);
    if (!trip) {
      throw new Error('Trip not found');
    }

    // Update status and timestamp
    trip.status = newStatus;
    trip.updatedAt = new Date().toISOString();

    if (newStatus === 'completed' || newStatus === 'archived') {
      trip.completedAt = new Date().toISOString();
    }

    // Save updated trip
    return await this.saveTrip(trip);
  }

  async deleteTrip(tripId) {
    if (!this.isInitialized) await this.initialize();

    // Delete trip
    const tripTx = this.db.transaction(['trips'], 'readwrite');
    await new Promise((resolve, reject) => {
      const request = tripTx.objectStore('trips').delete(tripId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // Delete all photos of this trip
    const photoTx = this.db.transaction(['photos', 'thumbnails'], 'readwrite');
    const photoStore = photoTx.objectStore('photos');
    const thumbStore = photoTx.objectStore('thumbnails');
    const photoIndex = photoStore.index('tripId');

    return new Promise((resolve, reject) => {
      const request = photoIndex.openCursor(IDBKeyRange.only(tripId));
      const photosToDelete = [];

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          photosToDelete.push(cursor.value.id);
          cursor.delete();
          cursor.continue();
        } else {
          // Delete thumbnails
          photosToDelete.forEach(photoId => {
            thumbStore.delete(photoId);
          });
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ========================================
  // PHOTOS (IndexedDB)
  // ========================================

  async savePhoto(photoData) {
    if (!this.isInitialized) await this.initialize();

    const transaction = this.db.transaction(['photos'], 'readwrite');
    const store = transaction.objectStore('photos');

    return new Promise((resolve, reject) => {
      const request = store.put(photoData);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPhoto(photoId) {
    if (!this.isInitialized) await this.initialize();

    const transaction = this.db.transaction(['photos'], 'readonly');
    const store = transaction.objectStore('photos');

    return new Promise((resolve, reject) => {
      const request = store.get(photoId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPhotosByTrip(tripId) {
    if (!this.isInitialized) await this.initialize();

    const transaction = this.db.transaction(['photos'], 'readonly');
    const store = transaction.objectStore('photos');
    const index = store.index('tripId');

    return new Promise((resolve, reject) => {
      const request = index.getAll(IDBKeyRange.only(tripId));
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  // Load only the most recent N photos to prevent OOM on mobile
  async getRecentPhotosByTrip(tripId, limit = 20) {
    if (!this.isInitialized) await this.initialize();
    const transaction = this.db.transaction(['photos'], 'readonly');
    const index = transaction.objectStore('photos').index('tripId');
    const photos = [];
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.only(tripId), 'prev');
      request.onsuccess = (e) => {
        const cursor = e.target.result;
        if (cursor && photos.length < limit) {
          photos.push(cursor.value);
          cursor.continue();
        } else {
          resolve(photos.reverse());
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get total photo count without loading data
  async getPhotosCountByTrip(tripId) {
    if (!this.isInitialized) await this.initialize();
    const transaction = this.db.transaction(['photos'], 'readonly');
    const index = transaction.objectStore('photos').index('tripId');
    return new Promise((resolve, reject) => {
      const request = index.count(IDBKeyRange.only(tripId));
      request.onsuccess = () => resolve(request.result || 0);
      request.onerror = () => reject(request.error);
    });
  }

  async deletePhoto(photoId) {
    if (!this.isInitialized) await this.initialize();

    const transaction = this.db.transaction(['photos', 'thumbnails'], 'readwrite');

    // Delete photo
    await new Promise((resolve, reject) => {
      const request = transaction.objectStore('photos').delete(photoId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // Delete thumbnail
    await new Promise((resolve, reject) => {
      const request = transaction.objectStore('thumbnails').delete(photoId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ========================================
  // THUMBNAILS (IndexedDB)
  // ========================================

  async saveThumbnail(photoId, thumbnailBlob) {
    if (!this.isInitialized) await this.initialize();

    const transaction = this.db.transaction(['thumbnails'], 'readwrite');
    const store = transaction.objectStore('thumbnails');

    return new Promise((resolve, reject) => {
      const request = store.put({ photoId, blob: thumbnailBlob });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getThumbnail(photoId) {
    if (!this.isInitialized) await this.initialize();

    const transaction = this.db.transaction(['thumbnails'], 'readonly');
    const store = transaction.objectStore('thumbnails');

    return new Promise((resolve, reject) => {
      const request = store.get(photoId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ========================================
  // STORAGE STATS
  // ========================================

  async getStorageStats() {
    if (!navigator.storage || !navigator.storage.estimate) {
      return {
        usage: 0,
        quota: 0,
        percentage: 0,
        usageGB: '0.00',
        quotaGB: '0.00',
        available: true
      };
    }

    try {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percentage = quota > 0 ? (usage / quota) * 100 : 0;

      return {
        usage: usage,
        quota: quota,
        percentage: percentage,
        usageGB: usage / (1024 * 1024 * 1024),
        quotaGB: quota / (1024 * 1024 * 1024),
        usageMB: usage / (1024 * 1024),
        quotaMB: quota / (1024 * 1024),
        available: percentage < 90
      };
    } catch (error) {
      console.error('Error getting storage stats:', error);
      return {
        usage: 0,
        quota: 0,
        percentage: 0,
        usageGB: '0.00',
        quotaGB: '0.00',
        available: true
      };
    }
  }

  async getTripStorageSize(tripId) {
    if (!this.isInitialized) await this.initialize();

    const photos = await this.getPhotosByTrip(tripId);
    let totalSize = 0;

    photos.forEach(photo => {
      if (photo.blob) {
        totalSize += photo.blob.size;
      }
    });

    return {
      tripId,
      photoCount: photos.length,
      sizeBytes: totalSize,
      sizeMB: (totalSize / (1024 * 1024)).toFixed(2),
      sizeKB: (totalSize / 1024).toFixed(2)
    };
  }

  // ========================================
  // METADATA (localStorage - backwards compatible)
  // ========================================

  // Active-trip pointer + id list, scoped per owner so each account has its own
  // active trip. Falls back to the legacy device-global key when not logged in.
  _tripsIndexKey() {
    const owner = this.getOwner();
    return owner ? `trips_index_${owner}` : 'trips_index';
  }

  getTripsIndex() {
    try {
      const item = localStorage.getItem(this._tripsIndexKey());
      const parsed = item ? JSON.parse(item) : null;
      if (parsed) {
        // Sprint 2 — legacy indexes have no `sealed` map; default it.
        if (!parsed.sealed) parsed.sealed = {};
        return parsed;
      }
      return {
        activeTrip: null,
        tripIds: [],
        sealed: {},
        lastModified: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting trips index:', error);
      return {
        activeTrip: null,
        tripIds: [],
        sealed: {},
        lastModified: new Date().toISOString()
      };
    }
  }

  // Sprint 2 — sealed flag lives in a parallel map so the archive can be listed
  // without loading whole trips. Additive: tripIds[] is untouched.
  markSealed(tripId, sealed = true) {
    const index = this.getTripsIndex();
    index.sealed = index.sealed || {};
    if (sealed) index.sealed[tripId] = true;
    else delete index.sealed[tripId];
    index.lastModified = new Date().toISOString();
    return this.saveTripsIndex(index);
  }

  isSealed(tripId) {
    return !!this.getTripsIndex().sealed?.[tripId];
  }

  saveTripsIndex(index) {
    try {
      localStorage.setItem(this._tripsIndexKey(), JSON.stringify(index));
      return true;
    } catch (error) {
      console.error('Error saving trips index:', error);
      return false;
    }
  }

  async ensureTripsIndexSync() {
    const index = this.getTripsIndex();
    if (index.tripIds.length === 0) {
      const allTrips = await this.getAllTrips();
      if (allTrips.length > 0) {
        const activeTrip = allTrips.find(t => t.status === 'active');
        this.saveTripsIndex({
          activeTrip: activeTrip?.tripId || null,
          tripIds: allTrips.map(t => t.tripId),
          lastModified: new Date().toISOString()
        });
        console.warn('[StorageManager] Recovered trips_index:', allTrips.length, 'trips');
        return true;
      }
    }
    return false;
  }

  getActiveTripId() {
    const index = this.getTripsIndex();
    return index.activeTrip;
  }

  async getActiveTrip() {
    const activeId = this.getActiveTripId();
    if (!activeId) return null;
    return await this.getTrip(activeId);
  }

  // ========================================
  // TRIP DATA (timeline, notes, etc.)
  // ========================================

  async getTripData(tripId) {
    const trip = await this.getTrip(tripId);
    if (!trip) {
      return {
        photos: [],
        notes: [],
        challenges: [],
        reflections: [],
        timeline: {},
        luggage: [],
        groceries: [],
        gifts: [],
        lodgings: [],
        privateDocuments: [],
        revealedPois: [],
        surfacedPois: [],
        dayClosures: {},
        // Sprint 2 — sealed book + open threads (additive)
        sealedAt: null,
        openThreads: [],
        travelInfo: {
          flights: [],
          cars: [],
          trains: [],
          buses: []
        }
      };
    }

    // Load only the most recent 20 photos to prevent OOM on mobile
    const [photos, photoCount] = await Promise.all([
      this.getRecentPhotosByTrip(tripId, 20),
      this.getPhotosCountByTrip(tripId)
    ]);

    // Return trip data structure
    return {
      photos: photos || [],
      totalPhotoCount: photoCount || 0,
      notes: (trip.notes || []).map(note => ({
        ...note,
        photos: (note.photos || []).map(p => ({ id: p.id, timestamp: p.timestamp }))
      })),
      challenges: trip.challenges || [],
      reflections: trip.reflections || [],
      timeline: trip.timeline || {},
      luggage: trip.luggage || [],
      groceries: trip.groceries || [],
      gifts: trip.gifts || [],
      lodgings: trip.lodgings || [],
      privateDocuments: trip.privateDocuments || [],
      // Sprint 1 — proximity reveal fields (legacy trips default to empty)
      revealedPois: trip.revealedPois || [],
      surfacedPois: trip.surfacedPois || [],
      dayClosures: trip.dayClosures || {},
      // Sprint 2 — sealed book + open threads (legacy trips default to unsealed)
      sealedAt: trip.sealedAt || null,
      openThreads: trip.openThreads || [],
      travelInfo: trip.travelInfo || {
        flights: [],
        cars: [],
        trains: [],
        buses: []
      }
    };
  }

  async getActiveTripData() {
    const activeId = this.getActiveTripId();
    if (!activeId) return null;
    return await this.getTripData(activeId);
  }

  async saveTripData(tripId, tripData) {
    const trip = await this.getTrip(tripId);
    if (!trip) {
      throw new Error(`Trip ${tripId} not found`);
    }

    // Strip base64 photo data from notes before saving — photos live in the photos store
    const notesWithoutPhotoData = (tripData.notes || trip.notes || []).map(note => ({
      ...note,
      photos: (note.photos || []).map(p => ({ id: p.id, timestamp: p.timestamp }))
    }));

    // Update trip with new data
    trip.notes = notesWithoutPhotoData;
    trip.challenges = tripData.challenges || trip.challenges || [];
    trip.reflections = tripData.reflections || trip.reflections || [];
    trip.timeline = tripData.timeline || trip.timeline || {};
    trip.luggage = tripData.luggage || trip.luggage || [];
    trip.groceries = tripData.groceries || trip.groceries || [];
    trip.gifts = tripData.gifts || trip.gifts || [];
    trip.lodgings = tripData.lodgings || trip.lodgings || [];
    trip.privateDocuments = tripData.privateDocuments || trip.privateDocuments || [];
    // Sprint 1 — proximity reveal fields (additive; legacy-safe)
    trip.revealedPois = tripData.revealedPois || trip.revealedPois || [];
    trip.surfacedPois = tripData.surfacedPois || trip.surfacedPois || [];
    trip.dayClosures = tripData.dayClosures || trip.dayClosures || {};
    // Sprint 2 — sealed book + open threads (additive; preserve null/empty)
    trip.sealedAt = (tripData.sealedAt !== undefined ? tripData.sealedAt : trip.sealedAt) ?? null;
    trip.openThreads = tripData.openThreads || trip.openThreads || [];
    trip.travelInfo = tripData.travelInfo || trip.travelInfo || {
      flights: [],
      cars: [],
      trains: [],
      buses: []
    };
    trip.updatedAt = new Date().toISOString();

    return await this.saveTrip(trip);
  }

  async saveActiveTripData(tripData) {
    const activeId = this.getActiveTripId();
    if (!activeId) {
      throw new Error('No active trip');
    }
    return await this.saveTripData(activeId, tripData);
  }

  // ========================================
  // FILE HANDLES (Chrome FSA handle persistence)
  // ========================================

  async saveFileHandle(tripId, handle) {
    if (!this.isInitialized) await this.initialize();
    const tx = this.db.transaction(['fileHandles'], 'readwrite');
    return new Promise((resolve, reject) => {
      const req = tx.objectStore('fileHandles').put(handle, tripId);
      req.onsuccess = () => resolve(true);
      req.onerror  = () => reject(req.error);
    });
  }

  async getFileHandle(tripId) {
    if (!this.isInitialized) await this.initialize();
    const tx = this.db.transaction(['fileHandles'], 'readonly');
    return new Promise((resolve, reject) => {
      const req = tx.objectStore('fileHandles').get(tripId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror  = () => reject(req.error);
    });
  }

  async getAllFileHandles() {
    if (!this.isInitialized) await this.initialize();
    const tx = this.db.transaction(['fileHandles'], 'readonly');
    return new Promise((resolve, reject) => {
      const store   = tx.objectStore('fileHandles');
      const result  = {};
      const cursor  = store.openCursor();
      cursor.onsuccess = (e) => {
        const c = e.target.result;
        if (c) { result[c.key] = c.value; c.continue(); }
        else resolve(result);
      };
      cursor.onerror = () => reject(cursor.error);
    });
  }

  async deleteFileHandle(tripId) {
    if (!this.isInitialized) await this.initialize();
    const tx = this.db.transaction(['fileHandles'], 'readwrite');
    return new Promise((resolve, reject) => {
      const req = tx.objectStore('fileHandles').delete(tripId);
      req.onsuccess = () => resolve(true);
      req.onerror  = () => reject(req.error);
    });
  }

  // ========================================
  // HELPERS
  // ========================================

  generateTripId() {
    return 'trip_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  generatePhotoId() {
    return 'photo_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  generateNoteId() {
    return 'note_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // ========================================
  // CLEANUP
  // ========================================

  async clearAllData() {
    if (!this.isInitialized) await this.initialize();

    const stores = ['trips', 'photos', 'thumbnails', 'voiceNotes'];

    for (const storeName of stores) {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);

      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }

    console.log('🗑️ All IndexedDB data cleared');
  }
}

// Export singleton instance
const storageManager = new StorageManager();
export default storageManager;
